import {
  Component,
  Input,
  forwardRef,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms'
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

@Component({
  selector: 'rms-date-range-slider',
  templateUrl: './date-range-slider.component.html',
  styleUrls: ['./date-range-slider.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeSliderComponent),
      multi: true
    }
  ],
  imports : [CommonModule, FormsModule]
})
export class DateRangeSliderComponent implements OnInit, ControlValueAccessor {
  @Input() minDate: Date = new Date(new Date().getFullYear() - 1, 0, 1);
  @Input() maxDate: Date = new Date();
  @Input() granularity: 'day' | 'month' = 'day';
  @Input() dateFormat: string = 'MMM dd, yyyy';

  // Internal range values
  startIndex: number = 0;
  endIndex: number = 0;
  maxIndex: number = 0;

  private onChange: any = () => { };
  private onTouched: any = () => { };

  ngOnInit(): void {
    if (this.granularity === 'month') {
      this.maxIndex = this.getMonthIndex(this.maxDate);
    } else {
      this.maxIndex = this.getDayIndex(this.maxDate);
    }
  }

  writeValue(value: { start: Date; end: Date }): void {
    if (value?.start && value?.end) {
      if (this.granularity === 'month') {
        this.startIndex = this.getMonthIndex(value.start);
        this.endIndex = this.getMonthIndex(value.end);
      } else {
        this.startIndex = this.getDayIndex(value.start);
        this.endIndex = this.getDayIndex(value.end);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Optional
  }

  onSliderChange(): void {
    const result = {
      start: this.granularity === 'month'
        ? this.getDateFromMonthIndex(this.startIndex)
        : this.getDateFromDayIndex(this.startIndex),
      end: this.granularity === 'month'
        ? this.getDateFromMonthIndex(this.endIndex)
        : this.getDateFromDayIndex(this.endIndex)
    };

    this.onChange(result);
    this.onTouched();
  }

  // Month-based indexing
  getMonthIndex(date: Date): number {
    return (date.getFullYear() - this.minDate.getFullYear()) * 12 + (date.getMonth() - this.minDate.getMonth());
  }

  getDateFromMonthIndex(index: number): Date {
    const date = new Date(this.minDate);
    date.setMonth(date.getMonth() + index);
    return date;
  }

  // Day-based indexing
  getDayIndex(date: Date): number {
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.floor((date.getTime() - this.minDate.getTime()) / msPerDay);
  }

  getDateFromDayIndex(index: number): Date {
    const date = new Date(this.minDate);
    date.setDate(this.minDate.getDate() + index);
    return date;
  }
  getFormattedStart(): string {
    const date = this.granularity === 'month'
      ? this.getDateFromMonthIndex(this.startIndex)
      : this.getDateFromDayIndex(this.startIndex);
    return new Intl.DateTimeFormat('en-US', this.getIntlOptions()).format(date);
  }

  getFormattedEnd(): string {
    const date = this.granularity === 'month'
      ? this.getDateFromMonthIndex(this.endIndex)
      : this.getDateFromDayIndex(this.endIndex);
    return new Intl.DateTimeFormat('en-US', this.getIntlOptions()).format(date);
  }

  private getIntlOptions(): Intl.DateTimeFormatOptions {
    switch (this.dateFormat) {
      case 'MMM yyyy': return { year: 'numeric', month: 'short' };
      case 'MMM dd, yyyy': return { year: 'numeric', month: 'short', day: '2-digit' };
      case 'MM/yyyy': return { year: 'numeric', month: '2-digit' };
      default: return { year: 'numeric', month: 'short', day: '2-digit' };
    }
  }
}
